/**
 * 车辆模块 API —— 对接 Java 后端（Spring Boot）REST 接口
 *
 * 假设 Java 后端 Controller 定义如下（示例）：
 *
 *   @RestController
 *   @RequestMapping("/api/listings")
 *   public class ListingController {
 *
 *     // GET /api/listings                 分页查询车辆
 *     // GET /api/listings/featured       查询精选车辆
 *     // GET /api/listings/latest         查询最新车辆
 *     // GET /api/listings/{id}           查询车辆详情
 *     // POST /api/listings               新增车辆
 *     // PUT /api/listings/{id}           更新车辆
 *     // DELETE /api/listings/{id}        删除车辆
 *   }
 *
 *   // GET /api/brands                   查询品牌列表
 */
import { apiClient, getToken } from '@/lib/api/client';

/** 获取当前登录用户信息（/auth/info，通过 rewrite 代理） */
export async function getAuthInfo() {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch('/auth/info', { method: 'GET', headers, cache: 'no-store' });
    let json = null;

    try { json = await res.json(); } catch (_) {}

    if (!res.ok) throw new Error(json?.message || `获取用户信息失败（HTTP ${res.status}）`);
    // json already parsed above
    if (json.code !== 200 && json.code !== 0) throw new Error(json.message || '获取用户信息失败');
    return json;
}
/** 分页查询车辆列表 */
export async function getListingsFromApi(params = {}, config) {
    var _a, _b;
    return apiClient.get('/listings', Object.assign(Object.assign({}, config), { params: {
            page: (_a = params.page) !== null && _a !== void 0 ? _a : 1,
            size: (_b = params.size) !== null && _b !== void 0 ? _b : 12,
            keyword: params.keyword,
            brand: params.brand,
            category: params.category,
            minPrice: params.minPrice,
            maxPrice: params.maxPrice,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
        } }));
}
/** 查询精选车辆（首页「精选推荐」） */
export async function getFeaturedListingsFromApi(config) {
    return apiClient.get('/listings/featured', config);
}
/** 查询最新车辆（首页「最新上架」） */
export async function getLatestListingsFromApi(limit = 8, config) {
    return apiClient.get('/listings/latest', Object.assign(Object.assign({}, config), { params: { limit } }));
}
/** 查询车辆详情 */
export async function getListingByIdFromApi(id, config) {
    return apiClient.get(`/listings/${id}`, config);
}
/** 获取编辑用车辆详情（GET /api/listing/{id} — 返回完整可编辑字段，含联系信息） */
export async function getListingEditDetail(id) {
    return apiClient.get(`/listing/${id}`);
}

/** 新增车辆（发布广告） */
export async function createListingFromApi(payload, config) {
    return apiClient.post('/listing', payload, config);
}
/** 更新车辆（PUT /api/listing/{id}） */
export async function updateListingFromApi(id, payload, config) {
    return apiClient.put(`/listing/${id}`, payload, config);
}
/** 删除车辆 */
export async function deleteListingFromApi(id, config) {
    return apiClient.delete(`/listings/${id}`, config);
}
/** 查询品牌列表 */
export async function getBrandsFromApi(config) {
    return apiClient.get('/brands', config);
}

/** 分页查询车辆列表（新接口 /api/listing/list） */
export async function getListingList(params = {}) {
    return apiClient.get('/listing/list', { params });
}

/** 查询筛选条件计数（/api/listing/filter-counts） */
export async function getFilterCounts(config) {
    return apiClient.get('/listing/filter-counts', config);
}

/** 获取我的广告列表（/api/listing/mine） */
export async function getMyListings(params = {}) {
    const res = await apiClient.get('/listing/mine', { params });
    console.log('[getMyListings] raw response:', res);
    if (res && Array.isArray(res.data)) {
        return res;
    }
    return res;
}

/** 获取收藏列表（/api/favorite/list） */
export async function getFavorites(params = {}) {
    const res = await apiClient.get('/favorite/list', { params });
    console.log('[getFavorites] raw response:', res);
    if (res && Array.isArray(res.data)) {
        return res;
    }
    return res;
}

/** 获取收藏我的车辆的列表（/api/favorite/received） */
export async function getFavoriteReceived(params = {}) {
    return apiClient.get('/favorite/received', { params });
}

/** 获取收藏我的车辆的未读列表（/api/favorite/received/unread） */
export async function getFavoriteReceivedUnread(params = {}) {
    return apiClient.get('/favorite/received/unread', { params });
}

/** 获取收藏我的未读数（/api/favorite/received/unread/count） */
export async function getFavoriteReceivedUnreadCount() {
    return apiClient.get('/favorite/received/unread/count');
}

/** 全部标记已读（POST /api/favorite/received/unread/read-all） */
export async function markAllFavoriteReceivedRead() {
    return apiClient.post('/favorite/received/unread/read-all');
}

/** 删除收藏（/api/favorite/delete/{id}） */
export async function deleteFavorite(id) {
    return apiClient.delete(`/favorite/delete/${id}`);
}

/** 收藏/取消收藏（POST /api/favorite） */
export async function toggleFavorite(listingId) {
    return apiClient.post('/favorite', { listingId });
}

/** 获取车辆详情（/api/listing/{id}/public） */
export async function getListingDetail(id) {
    return apiClient.get(`/listing/${id}/public`);
}

/** 获取数据看板统计（/api/listing/mine/stats） */
export async function getMyStats() {
    return apiClient.get('/listing/mine/stats');
}

/** 获取收到的询价列表（/api/inquiry/received） */
export async function getInquiries(params = {}) {
    return apiClient.get('/inquiry/received', { params });
}

/** 获取"询价我的车"会话列表（GET /api/message/conversations/user） */
export async function getInquiriesUser(params = {}) {
    return apiClient.get('/message/conversations/user', { params });
}

/** 获取我询价的车辆会话列表（GET /api/message/conversations/listing） */
export async function getInquiriesSent(params = {}) {
    return apiClient.get('/message/conversations/listing', { params });
}

/** 提交询价（POST /api/inquiry） */
export async function createInquiry(payload) {
    return apiClient.post('/inquiry', payload);
}

/** 获取品牌列表（GET /api/dict/brand） */
export async function getBrandList() {
    return apiClient.get('/dict/brand');
}

/** 获取品类列表（GET /api/category/list?type=car） */
export async function getCategoryList(type = 'car') {
    return apiClient.get('/category/list', { params: { type } });
}

/** 获取车辆分类列表（GET /api/listing/categories） */
export async function getListingCategories(parentId = 1) {
    return apiClient.get('/listing/categories', { params: { parentId } });
}

/** 获取字典配置（GET /api/dict/config） */
export async function getDictConfig(group) {
    return apiClient.get('/dict/config', { params: { group } });
}

/** 获取城市列表（GET /api/dict/city） */
export async function getCityList() {
    return apiClient.get('/dict/city');
}

/** 批量上传图片（/file/upload/batch，通过 rewrite 代理）
 *  @param {string} categoryType
 *  @param {number} objectId
 *  @param {File[]} files
 *  @param {(progress: number) => void} [onProgress] — 可选，0~100 上传进度回调
 */
export async function uploadImages(categoryType, objectId, files, onProgress) {
    const formData = new FormData();
    formData.append('categoryType', categoryType);
    formData.append('objectId', String(objectId));
    files.forEach((file) => {
        formData.append('files', file);
    });
    const token = getToken();

    // 无进度回调 → 继续用 fetch（保持原逻辑）
    if (!onProgress) {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch('/file/upload/batch', { method: 'POST', headers, body: formData });
        if (!res.ok) {
            let json = null;
            try { json = await res.json(); } catch (_) {}
            throw new Error(json?.message || `上传失败（HTTP ${res.status}）`);
        }
        const json = await res.json();
        if (json.code !== 200) throw new Error(json.message || '上传失败');
        return json;
    }

    // 有进度回调 → 用 XMLHttpRequest（fetch 不支持上传进度）
    return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/file/upload/batch', true);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                const p = Math.round((e.loaded / e.total) * 100);
                onProgress(Math.min(p, 99)); // 上传阶段不超过 99%
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const json = JSON.parse(xhr.responseText);
                    if (json.code !== 200) {
                        reject(new Error(json.message || '上传失败'));
                    } else {
                        onProgress(100);
                        resolve(json);
                    }
                } catch (_) {
                    reject(new Error('服务器返回格式错误'));
                }
            } else {
                try {
                    const json = JSON.parse(xhr.responseText);
                    reject(new Error(json?.message || `上传失败（HTTP ${xhr.status}）`));
                } catch (_) {
                    reject(new Error(`上传失败（HTTP ${xhr.status}）`));
                }
            }
        };
        xhr.onerror = () => reject(new Error('网络错误，上传失败'));
        xhr.onabort = () => reject(new Error('上传已取消'));
        xhr.send(formData);
    });
}

/** 上传单张头像（POST /file/upload）
 *  服务端字段约定：categoryType、objectId、file
 */
export async function uploadAvatar(file, objectId) {
    const formData = new FormData();
    formData.append('categoryType', 'avatar');
    formData.append('objectId', String(objectId ?? 0));
    formData.append('file', file);
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch('/file/upload', { method: 'POST', headers, body: formData });
    let json = null;

    try { json = await res.json(); } catch (_) {}

    if (!res.ok) throw new Error(json?.message || `头像上传失败（HTTP ${res.status}）`);
    // json already parsed above
    if (json.code !== 200 && json.code !== 0) throw new Error(json.message || '头像上传失败');
    return json;
}

/** 用户名查重（GET /auth/check-username?username=xxx）
 *  返回约定：code=200 表示可用；其他表示已存在或错误
 */
export async function checkUsername(username) {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`/auth/check-username?username=${encodeURIComponent(username)}`, { method: 'GET', headers });
    let json = null;

    try { json = await res.json(); } catch (_) {}

    if (!res.ok) throw new Error(json?.message || `校验失败（HTTP ${res.status}）`);
    // json already parsed above
    return json;
}

/** 更新登录用户资料（POST /auth/update） */
export async function updateAuth(payload) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // 只保留有值字段，避免后端收到空字符串
    const cleaned = {};
    Object.entries(payload || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') cleaned[k] = v;
    });
    const res = await fetch('/auth/update', {
        method: 'POST',
        headers,
        body: JSON.stringify(cleaned),
    });
    let json = null;
    try { json = await res.json(); } catch (_) { /* ignore parse error */ }
    if (!res.ok) {
        const msg = json?.message || `更新失败（HTTP ${res.status}）`;
        const err = new Error(msg);
        err.code = json?.code;
        err.status = res.status;
        throw err;
    }
    if (json && json.code !== 200 && json.code !== 0) {
        const err = new Error(json.message || '更新失败');
        err.code = json.code;
        throw err;
    }
    return json;
}

/** 对外统一导出 */
export const listingApi = {
    getAuthInfo,
    list: getListingsFromApi,
    featured: getFeaturedListingsFromApi,
    latest: getLatestListingsFromApi,
    detail: getListingByIdFromApi,
    create: createListingFromApi,
    update: updateListingFromApi,
    editDetail: getListingEditDetail,
    remove: deleteListingFromApi,
    listingList: getListingList,
    filterCounts: getFilterCounts,
    myListings: getMyListings,
    favorites: getFavorites,
    favoriteReceived: getFavoriteReceived,
    favoriteReceivedUnread: getFavoriteReceivedUnread,
    favoriteReceivedUnreadCount: getFavoriteReceivedUnreadCount,
    markAllFavoriteReceivedRead,
    deleteFavorite,
    toggleFavorite,
    listingDetail: getListingDetail,
    myStats: getMyStats,
    inquiries: getInquiries,
    inquiriesUser: getInquiriesUser,
    inquiriesSent: getInquiriesSent,
    createInquiry,
    getBrandList,
    getListingCategories,
    getDictConfig,
    getCityList,
    uploadImages,
    uploadAvatar,
    checkUsername,
    updateAuth,
    notificationList: getNotificationList,
    messageConversations: getMessageConversations,
    messageConversation: getMessageConversation,
    sendMessage: sendMessageApi,
    messageUnreadSummary: getMessageUnreadSummary,
    promotionPlans: getPromotionPlans,
    promotionMyOrders: getPromotionMyOrders,
    similarListings: getSimilarListings,
    searchSuggest: getSearchSuggest,
};

/** 通知列表（GET /api/notification/list） */
export async function getNotificationList(params = {}) {
    const { pageNum = 1, pageSize = 10 } = params;
    return apiClient.get('/notification/list', { params: { pageNum, pageSize } });
}

/** 消息会话列表（GET /api/message/conversations） */
export async function getMessageConversations() {
    return apiClient.get('/message/conversations');
}

/** 对话历史消息（GET /api/message/conversation） */
export async function getMessageConversation({ otherUserId, listingId, pageNum = 1, pageSize = 20 } = {}) {
    const params = { otherUserId, pageNum, pageSize };
    if (listingId != null) params.listingId = listingId;
    return apiClient.get('/message/conversation', { params });
}

/** 发送消息（POST /api/message） */
export async function sendMessageApi({ receiverId, listingId, content }) {
    const payload = { receiverId, content };
    if (listingId != null) payload.listingId = listingId;
    return apiClient.post('/message', payload);
}

/** 未读消息汇总（GET /api/message/unread-summary）— 返回 { totalUnread, message, notification, favorite, reply } */
export async function getMessageUnreadSummary() {
    return apiClient.get('/message/unread-summary');
}

/** 推广套餐列表（GET /api/promotion/plans） */
export async function getPromotionPlans() {
    return apiClient.get('/promotion/plans');
}

/** 推广订单列表（GET /api/promotion/my-orders） */
export async function getPromotionMyOrders() {
    return apiClient.get('/promotion/my-orders');
}

/** 相似车辆推荐（GET /api/listing/{id}/similar） */
export async function getSimilarListings(id) {
    return apiClient.get(`/listing/${id}/similar`);
}



/** search suggest */
export async function getSearchSuggest(keyword, limit) {
    return apiClient.get('/search/suggest', { params: { keyword, limit: limit || 10 } });
}
export default listingApi;
